/**
 * @fileoverview O passo da equipe na esteira de cadastro: convidar por e-mail
 * quem vai atender (o convite é para a conta ativa, vale 7 dias e uma vez). O
 * servidor pede o e-mail de quem convida confirmado: antes disso, o aviso com o
 * reenvio. Convidar pede team.manage, e convidar um administrador também
 * team.manage_access, como em Usuários.
 */

import React, { useContext, useEffect, useState } from "react";
import CircularProgress from "@material-ui/core/CircularProgress";
import MenuItem from "@material-ui/core/MenuItem";

import ModernSelect from "../ModernSelect";
import { useAuthFormStyles } from "../AuthLayout/formStyles";
import { EMAIL_RE, errorText } from "../AuthLayout/authHelpers";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePermission, { isAdminUser } from "../../hooks/usePermission";
import api, { openApi } from "../../services/api";
import { i18n } from "../../translate/i18n";
import useSetupStyles from "./styles";
import { SetupInfo } from "./SetupProgress";

const t = (key, options) => i18n.t(`companySetup.team.${key}`, options);

const TeamStep = ({ emailVerified, onNext }) => {
  const form = useAuthFormStyles();
  const classes = useSetupStyles();
  const { user } = useContext(AuthContext);
  const { can } = usePermission();
  const canInvite = can("team.manage", isAdminUser(user));
  const canInviteAdmin = can("team.manage_access", isAdminUser(user));
  const [invites, setInvites] = useState([]);
  const [email, setEmail] = useState("");
  const [profile, setProfile] = useState("user");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    if (!canInvite) return undefined;
    let alive = true;
    api
      .get("/invites")
      .then(({ data }) => alive && setInvites(Array.isArray(data) ? data.filter((invite) => invite.status === "pending") : []))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [canInvite]);

  const send = async (event) => {
    event.preventDefault();
    const address = email.trim();
    if (!EMAIL_RE.test(address)) {
      setError(t("emailInvalid"));
      return;
    }
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const { data } = await api.post("/invites", { email: address, profile });
      setInvites((current) => [data, ...current.filter((invite) => invite.id !== data.id)]);
      setNotice(t("sent", { email: data.email }));
      setEmail("");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const resendConfirmation = async () => {
    setError("");
    try {
      await openApi.post("/auth/verify-email/resend", { email: user.email });
      setNotice(t("resent"));
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <div className={`${classes.step} ${form.view}`}>
      <h1 className={form.title}>{t("title")}</h1>
      <p className={form.subtitle}>{t("subtitle")}</p>

      {!canInvite ? (
        <p className={classes.howTo}>{t("noPermission")}</p>
      ) : (
        <>
          {!emailVerified && (
            <div className={form.notice}>
              {t("verifyFirst", { email: user.email })}{" "}
              <button type="button" className={form.link} onClick={resendConfirmation}>
                {t("resend")}
              </button>
            </div>
          )}
          <form onSubmit={send} noValidate>
            <div className={classes.inviteRow}>
              <div>
                <label className={form.label} htmlFor="invite-email">{t("email")}</label>
                <input
                  id="invite-email"
                  type="email"
                  className={form.input}
                  value={email}
                  placeholder={t("emailPlaceholder")}
                  autoComplete="off"
                  aria-invalid={error ? "true" : "false"}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>
              <div>
                <div className={classes.labelRow}>
                  <span className={`${form.label} ${classes.labelInRow}`} id="invite-profile-label">{t("profile")}</span>
                  <SetupInfo text={t("profileInfo")} />
                </div>
                <ModernSelect
                  className={classes.select}
                  fullWidth
                  value={profile}
                  onChange={(event) => setProfile(event.target.value)}
                  labelId="invite-profile-label"
                >
                  <MenuItem value="user">{t("profiles.user")}</MenuItem>
                  {canInviteAdmin && <MenuItem value="admin">{t("profiles.admin")}</MenuItem>}
                </ModernSelect>
              </div>
              <button type="submit" className={`${form.button} ${classes.inviteButton}`} disabled={busy || !email.trim()}>
                {busy && <CircularProgress size={16} className={form.spinner} />}
                {t("send")}
              </button>
            </div>
            {error && <div className={form.error} role="alert">{error}</div>}
            {notice && !error && <div className={form.hint} role="status">{notice}</div>}
          </form>

          {invites.length > 0 && (
            <ul className={classes.list} aria-label={t("invited")}>
              {invites.map((invite) => (
                <li key={invite.id} className={classes.listItem}>
                  <div className={classes.listMain}>
                    <div className={classes.listTitle}>{invite.email}</div>
                    <div className={classes.listMeta}>{t(`profiles.${invite.profile === "admin" ? "admin" : "user"}`)}</div>
                  </div>
                  <span className={`${classes.status} ${classes.statusWait}`}>
                    <span className={classes.statusDot} aria-hidden="true" />
                    {t("pending")}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <div className={classes.actions}>
        {invites.length > 0 ? (
          <button type="button" className={`${form.button} ${classes.actionButton}`} onClick={() => onNext(invites.length)}>
            {t("next")}
          </button>
        ) : (
          <button type="button" className={`${form.secondary} ${classes.actionSecondary}`} onClick={() => onNext(0)}>
            {t("later")}
          </button>
        )}
      </div>
    </div>
  );
};

export default TeamStep;
